import type { FC } from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { CommentButton } from '@/features/posts/components/comments/CommentButton';
import { PollChip } from '@/features/posts/components/detail/poll/PollChip';
import { usePostCard } from '@/features/posts/components/feed/PostCardContext';
import { PostLikeButton } from '@/features/posts/components/PostLikeButton';
import useIsPostcardNew from '@/features/posts/hooks/useIsNewPostCard';
import { postCardActionsStyle } from '@/features/posts/styles/postCardPointer';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    card: {
      marginBottom: 12,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    inner: { paddingTop: 0, paddingHorizontal: 0 },
    actions: {
      ...postCardActionsStyle,
      gap: 12,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginLeft: 8,
      marginVertical: 8
    },
    blocked: { fontStyle: 'italic', marginBottom: 16 },
    new: {
      marginLeft: 'auto',
      marginRight: 8,
      fontWeight: 600,
      alignSelf: 'flex-end',
      color: theme.colors.error
    }
  });

export const PostCardActions: FC = memo(() => {
  const { isBlocked, commentCount, poll, likeCount, liked, postId } =
    usePostCard();
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const isNew = useIsPostcardNew();

  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  if (isBlocked) {
    return;
  }

  return (
    <View style={styles.actions}>
      <CommentButton postId={postId} commentCount={commentCount} />
      {poll && <PollChip postId={postId} voterCount={poll?.voterCount} />}
      <PostLikeButton postId={postId} liked={liked} likeCount={likeCount} />
      {isNew && (
        <Text variant="labelLarge" style={styles.new}>
          {t('new')}
        </Text>
      )}
    </View>
  );
});
PostCardActions.displayName = 'PostCardActions';
